import React from 'react'
import PropTypes from 'prop-types'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getPaginationRowModel
} from '@tanstack/react-table'
import { makeStyles } from '@material-ui/core'

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    fontSize: '14px'
  },
  tableWrapper: {
    flex: 1,
    overflow: 'auto',
    fontWeight: 'var(--font-weight-thin)',
    minHeight: 0 // Important for flexbox overflow
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'monospace'
  },
  thead: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#f5f5f5',
    zIndex: 1
  },
  th: {
    padding: '8px 12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    borderRight: '1px solid #ddd',
    fontWeight: 600,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#e8e8e8'
    },
    '&:last-child': {
      borderRight: 'none'
    }
  },
  td: {
    padding: '6px 12px',
    borderBottom: '1px solid #eee',
    borderRight: '1px solid #eee',
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:last-child': {
      borderRight: 'none'
    }
  },
  sortIcon: {
    marginLeft: '4px',
    fontSize: '12px',
    verticalAlign: 'middle'
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderTop: '2px solid #ddd',
    backgroundColor: '#f9f9f9',
    flexShrink: 0
  },
  paginationInfo: {
    fontSize: '14px',
    color: '#666'
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  paginationButton: {
    padding: '4px 8px',
    border: '1px solid #ddd',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#f0f0f0'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },
  pageInput: {
    width: '50px',
    padding: '4px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    textAlign: 'center'
  },
  pageSizeSelect: {
    padding: '4px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  }
}))

function QueryResultTable ({ columns, data, defaultSort = [] }) {
  const classes = useStyles()

  const [sorting, setSorting] = React.useState(defaultSort)
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 100
  })

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination
    }
  })

  const pageCount = table.getPageCount()
  const currentPage = pagination.pageIndex + 1

  return (
    <div className={classes.container}>
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead className={classes.thead}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className={classes.th}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      width: header.column.getSize() !== 150 ? header.column.getSize() : undefined,
                      cursor: header.column.getCanSort() ? 'pointer' : 'default'
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() && (
                          <span className={classes.sortIcon}>
                            {header.column.getIsSorted() === 'desc' ? '▼' : '▲'}
                          </span>
                        )}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className={classes.td}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={classes.pagination}>
        <div className={classes.paginationInfo}>
          Showing {pagination.pageIndex * pagination.pageSize + 1} to{' '}
          {Math.min((pagination.pageIndex + 1) * pagination.pageSize, data.length)} of {data.length} rows
        </div>

        <div className={classes.paginationControls}>
          <button
            className={classes.paginationButton}
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {'<<'}
          </button>
          <button
            className={classes.paginationButton}
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {'<'}
          </button>

          <span>
            Page{' '}
            <input
              type='number'
              value={currentPage}
              onChange={e => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0
                table.setPageIndex(page)
              }}
              className={classes.pageInput}
              min={1}
              max={pageCount}
            />
            {' '}of {pageCount}
          </span>

          <button
            className={classes.paginationButton}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {'>'}
          </button>
          <button
            className={classes.paginationButton}
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {'>>'}
          </button>

          <select
            value={pagination.pageSize}
            onChange={e => {
              table.setPageSize(Number(e.target.value))
            }}
            className={classes.pageSizeSelect}
          >
            {[10, 25, 50, 100, 500, 1000].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}

QueryResultTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  defaultSort: PropTypes.array
}

export default QueryResultTable
